import {
  Box,
  Pagination,
  PaginationItem,
  Skeleton,
  Theme,
  useMediaQuery,
} from "@mui/material";
import {
  actionButtonSx,
  avatarSx,
  columnHeaderSx,
  emailCellSx,
  emptyStateSx,
  memberCardActionSx,
  memberCardAvatarSx,
  memberCardEmailSx,
  memberCardNameRowSx,
  memberCardNameSx,
  memberCardPhoneSx,
  memberCardPillSx,
  memberCardSx,
  memberCardTextSx,
  nameCellSx,
  nameTextSx,
  skeletonAvatarSx,
  paginationBarSx,
  paginationSx,
  phoneCellSx,
  rolePillSx,
  rowSx,
  statusSx,
  tableCardSx,
  tableInnerSx,
  tableScrollSx,
  visuallyHiddenSx,
} from "screens/Settings/Team/Team.styles";
import { userStatus } from "types/perSeat";
import {
  PagerNextIcon,
  PagerPrevIcon,
} from "screens/Settings/Team/components/TeamIcons";
import EmptyState from "global/components/EmptyState/EmptyState";

export interface TeamMemberRow {
  id: number;
  name: string;
  /** Two-letter monogram shown in the avatar. */
  initials: string;
  phone: string;
  email: string;
  /** First role name, suffixed with "+N" when the member holds several. */
  roleLabel: string;
  status: string;
}

/** An invited user is billable already, so it reads as "Invited", not pending. */
const statusLabel = (status: string) => {
  if (status === userStatus.inactive) return "In Active";
  if (status === userStatus.pendingVerification) return "Invited";
  return status;
};

interface TeamTableProps {
  members: TeamMemberRow[];
  loading: boolean;
  rowsPerPage: number;
  page: number;
  count: number;
  /** Omitted for members without admin rights — the column stays reserved. */
  onActionClick?: (
    event: React.MouseEvent<HTMLElement>,
    member: TeamMemberRow,
  ) => void;
  /** Id of the member whose action menu is currently open, for aria-expanded. */
  openMenuMemberId?: number | null;
  onPageChange: (event: React.ChangeEvent<unknown>, page: number) => void;
}

const COLUMNS = ["NAME", "PHONE", "EMAIL", "ROLE", "STATUS"];

/**
 * Team member list exactly as specified by the Settings design: a six-column
 * grid inside a single rounded card, closed by a right-aligned pager. All
 * geometry comes from `Team.tokens.ts`.
 */
const TeamTable = ({
  members,
  loading,
  rowsPerPage,
  page,
  count,
  onActionClick,
  openMenuMemberId,
  onPageChange,
}: TeamTableProps) => {
  const pageCount = Math.ceil((count || 0) / rowsPerPage);
  // Both small references replace the six-column grid with one card per member.
  const isCompact = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("md"),
  );

  const getRow = (member: TeamMemberRow, index: number) => {
    const menuOpen = openMenuMemberId != null && openMenuMemberId === member.id;
    return (
      <Box role="row" key={member.id} sx={rowSx(index === members.length - 1)}>
        <Box role="cell" sx={nameCellSx}>
          <Box component="span" sx={avatarSx} aria-hidden="true">
            {member.initials}
          </Box>
          <Box component="span" sx={nameTextSx} title={member.name}>
            {member.name}
          </Box>
        </Box>

        <Box role="cell" component="span" sx={phoneCellSx}>
          {member.phone}
        </Box>

        <Box role="cell" component="span" sx={emailCellSx} title={member.email}>
          {member.email}
        </Box>

        <Box role="cell" component="span" sx={{ minWidth: 0 }}>
          {member.roleLabel && (
            <Box component="span" sx={rolePillSx}>
              {member.roleLabel}
            </Box>
          )}
        </Box>

        <Box role="cell" component="span" sx={statusSx(member.status)}>
          {statusLabel(member.status)}
        </Box>

        <Box role="cell" component="span">
          {onActionClick && (
            <Box
              component="button"
              type="button"
              aria-label={`Actions for ${member.name}`}
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              aria-controls={menuOpen ? "simple-menu" : undefined}
              onClick={(event: React.MouseEvent<HTMLElement>) => {
                event.stopPropagation();
                onActionClick(event, member);
              }}
              sx={actionButtonSx}
            >
              ⋮
            </Box>
          )}
        </Box>
      </Box>
    );
  };

  const getCard = (member: TeamMemberRow) => {
    const menuOpen = openMenuMemberId != null && openMenuMemberId === member.id;
    return (
      <Box key={member.id} sx={memberCardSx}>
        <Box component="span" sx={memberCardAvatarSx} aria-hidden="true">
          {member.initials}
        </Box>

        <Box sx={memberCardTextSx}>
          <Box sx={memberCardNameRowSx}>
            <Box component="span" sx={memberCardNameSx}>
              {member.name}
            </Box>
            <Box component="span" sx={statusSx(member.status)}>
              {statusLabel(member.status)}
            </Box>
          </Box>
          <Box component="span" sx={memberCardEmailSx}>
            {member.email}
          </Box>
          {member.phone && (
            <Box component="span" sx={memberCardPhoneSx}>
              {member.phone}
            </Box>
          )}
          {member.roleLabel && (
            <Box component="span" sx={memberCardPillSx}>
              {member.roleLabel}
            </Box>
          )}
        </Box>

        {onActionClick && (
          <Box
            component="button"
            type="button"
            aria-label={`Actions for ${member.name}`}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            aria-controls={menuOpen ? "simple-menu" : undefined}
            onClick={(event: React.MouseEvent<HTMLElement>) => {
              event.stopPropagation();
              onActionClick(event, member);
            }}
            sx={memberCardActionSx}
          >
            ⋮
          </Box>
        )}
      </Box>
    );
  };

  const getLoadingCards = () =>
    [...Array(3)].map((_, index) => (
      <Box key={index} sx={memberCardSx} aria-hidden="true">
        <Skeleton variant="circular" width={38} height={38} />
        <Box sx={memberCardTextSx}>
          <Skeleton variant="text" width="50%" height={18} />
          <Skeleton variant="text" width="70%" height={14} />
          <Skeleton variant="text" width="40%" height={14} />
        </Box>
      </Box>
    ));

  const getLoadingRows = () =>
    [...Array(rowsPerPage)].map((_, index) => (
      <Box role="row" key={index} sx={rowSx(index === rowsPerPage - 1)}>
        {/*
          The name cell carries the avatar, and the avatar — not the text — is
          what sets a real row's height. A flat text bar here made the loading
          rows 45px against a real 55px, so the table grew by 10px a row as the
          members arrived.
        */}
        <Box role="cell" sx={nameCellSx}>
          <Skeleton variant="circular" sx={skeletonAvatarSx} />
          <Skeleton variant="text" width="70%" />
        </Box>
        <Skeleton role="cell" variant="text" width="75%" height={18} />
        <Skeleton role="cell" variant="text" width="85%" height={18} />
        <Skeleton role="cell" variant="text" width="60%" height={18} />
        <Skeleton role="cell" variant="text" width="55%" height={18} />
        <Box role="cell" />
      </Box>
    ));

  return (
    <Box sx={tableCardSx}>
      {isCompact ? (
        <Box role="list" aria-label="Team members" aria-busy={loading}>
          {loading ? (
            getLoadingCards()
          ) : members.length > 0 ? (
            members.map((member) => (
              <Box role="listitem" key={member.id} sx={{ display: "contents" }}>
                {getCard(member)}
              </Box>
            ))
          ) : (
            <EmptyState
              size="medium"
              icon="team"
              accent="violet"
              title="You are the only member"
              body="Invite colleagues to send and track requests under one organization."
            />
          )}
        </Box>
      ) : (
        <Box sx={tableScrollSx}>
          <Box
            role="table"
            aria-label="Team members"
            aria-busy={loading}
            sx={tableInnerSx}
          >
            <Box role="row" sx={columnHeaderSx}>
              {COLUMNS.map((column) => (
                <Box role="columnheader" component="span" key={column}>
                  {column}
                </Box>
              ))}
              <Box role="columnheader" component="span">
                <Box component="span" sx={visuallyHiddenSx}>
                  Actions
                </Box>
              </Box>
            </Box>

            {loading ? (
              getLoadingRows()
            ) : members.length > 0 ? (
              members.map(getRow)
            ) : (
              <Box role="row">
                <Box role="cell">
                  <EmptyState
                    icon="team"
                    accent="violet"
                    title="You are the only member"
                    body="Invite colleagues to send and track requests under one organization."
                  />
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      )}

      {!loading && pageCount > 0 && (
        <Box sx={paginationBarSx}>
          <Pagination
            page={page}
            count={pageCount}
            siblingCount={1}
            boundaryCount={1}
            shape="rounded"
            onChange={onPageChange}
            sx={paginationSx}
            renderItem={(item) => (
              <PaginationItem
                slots={{ previous: PagerPrevIcon, next: PagerNextIcon }}
                {...item}
              />
            )}
          />
        </Box>
      )}
    </Box>
  );
};

export default TeamTable;
